import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, User, Eye, Tag, List } from 'lucide-react';
import { Blog } from '../../types/blog';
import { blogService } from '../../services/blogService';

interface BlogCardProps {
  blog: Blog;
  variant?: 'default' | 'featured' | 'compact';
  showAuthor?: boolean;
  showViewCount?: boolean;
  showTags?: boolean;
  className?: string;
}

export const BlogCard: React.FC<BlogCardProps> = ({
  blog,
  variant = 'default',
  showAuthor = true,
  showViewCount = false,
  showTags = true,
  className = ''
}) => {
  const formatDate = (date: Date | string | undefined) => {
    if (!date) return '';
    return blogService.formatDate(date);
  };

  const getAuthorName = () => {
    return `${blog.author.firstName} ${blog.author.lastName}`.trim();
  };

  const handleCardClick = () => {
    // Track analytics when blog card is clicked
    // You can add analytics tracking here
  };

  const cardSizeClasses = {
    default: 'group cursor-pointer transition-all duration-300 hover:shadow-sm hover:-translate-y-1',
    featured: 'group cursor-pointer transition-all duration-500 hover:shadow-sm hover:-translate-y-2',
    compact: 'group cursor-pointer transition-all duration-200 hover:shadow-sm hover:-translate-y-0.5'
  };

  const imageHeightClasses = {
    default: 'h-52',
    featured: 'h-64',
    compact: 'h-34'
  };

  return (
    <article className={`${cardSizeClasses[variant]} ${className} h-full`}>
      <Link 
        to={`/blog/${blog.slug}`} 
        onClick={handleCardClick}
        className="block h-full"
      >
        <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-sm overflow-hidden h-full border border-border-light dark:border-border-dark flex flex-col relative hover:shadow-lg transition-all duration-300 ">
          {/* Image Section */}
          <div className={`relative overflow-hidden ${imageHeightClasses[variant]} flex-shrink-0`}>
            {blog.featuredImage ? (
              <>
                <img
                  src={blog.featuredImage}
                  alt={blog.imageAlt || blog.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                {/* Featured Badge */}
                {blog.featured && variant === 'featured' && (
                  <div className="absolute top-4 left-4">
                    <span className="bg-primary-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                      Featured
                    </span>
                  </div>
                )}
              </>
            ) : (
              /* No Image State - Beautiful Gradient with Content */
              <div className="w-full h-full bg-gradient-to-br from-primary-500 via-primary-600 to-primary-700 flex items-center justify-center relative overflow-hidden">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent transform -skew-x-12"></div>
                </div>
                
                {/* Content */}
                <div className="text-center p-6 relative z-10">
                  <div className="bg-background-light dark:bg-background-dark backdrop-blur-sm rounded-lg p-4 border border-white/30">
                    <h3 className="text-text-primary-light dark:text-text-primary-dark font-heading font-bold text-lg mb-2 line-clamp-2">
                      {blog.title}
                    </h3>
                    <div className="flex items-center justify-center space-x-4 text-text-secondary-light dark:text-text-secondary-dark text-sm">
                      <div className="flex items-center space-x-1">
                        <Clock className="w-4 h-4" />
                        <span>{blog.readTimeMinutes} min read</span>
                      </div>
                      {showViewCount && blog.viewCount > 0 && (
                        <div className="flex items-center space-x-1">
                          <Eye className="w-4 h-4" />
                          <span>{blog.viewCount}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Featured Badge for no-image state */}
                {blog.featured && variant === 'featured' && (
                  <div className="absolute top-4 left-4">
                    <span className="bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm font-medium border border-white/30">
                      Featured
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Content Section */}
          <div className="p-4 md:p-6 flex flex-col flex-grow">
            {/* Categories */}
            {blog.categories.length > 0 && (
              <div className="mb-3 flex items-center space-x-2">
                <List className="w-4 h-4 text-primary-light dark:text-primary-dark" />
                <span className="inline-block bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 py-1 rounded-full text-sm font-medium">
                  {blog.categories[0]}
                </span>
              </div>
            )}

            {/* Title */}
            <h3 className="font-heading font-bold text-xl mb-3 text-text-primary-light dark:text-text-primary-dark group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-300 line-clamp-2">
              {blog.title}
            </h3>

            {/* Excerpt */}
            <p className="text-text-secondary-light dark:text-text-secondary-dark mb-4 text-base line-clamp-3 leading-relaxed flex-grow">
              {blog.excerpt}
            </p>

            {/* Meta Information */}
            <div className="flex items-center justify-between text-sm text-text-secondary-light dark:text-text-secondary-dark mb-4">
              <div className="flex items-center space-x-4">
                {showAuthor && (
                  <div className="flex items-center space-x-1">
                    <User className="w-4 h-4" />
                    <span>{getAuthorName()}</span>
                  </div>
                )}
                
                {blog.publishedAt && (
                  <div className="flex items-center space-x-1.5">
                    <Calendar className="w-4 h-4 mb-0.5 text-primary-light dark:text-primary-dark" />
                    <span>{formatDate(blog.publishedAt)}</span>
                  </div>
                )}

                <div className="flex items-center space-x-1.5">
                  <Clock className="w-4 h-4 text-primary-light dark:text-primary-dark" />
                  <span>{blog.readTimeMinutes} min read</span>
                </div>
              </div>

              {showViewCount && blog.viewCount > 0 && (
                <div className="flex items-center space-x-1.5">
                  <Eye className="w-4 h-4 text-primary-light dark:text-primary-dark" />
                  <span>{blog.viewCount.toLocaleString()}</span>
                </div>
              )}
            </div>

            {/* Tags */}
            {showTags && blog.tags.length > 0 && (
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700 mt-auto">
                <div className="flex items-start gap-2">
                  <Tag className="w-4 h-4 text-primary-light dark:text-primary-dark mt-1 flex-shrink-0" />
                  <div className="flex flex-wrap gap-2">
                    {blog.tags.slice(0, 3).map((tag, index) => (
                      <span
                        key={index}
                        className="text-xs bg-background-light dark:bg-background-dark text-text-primary-light dark:text-text-primary-dark px-2 py-1 rounded-md"
                      >
                        {tag.charAt(0).toUpperCase() + tag.slice(1)}
                      </span>
                    ))}
                    {blog.tags.length > 3 && (
                      <span className="text-xs text-text-secondary-light dark:text-text-secondary-dark self-center">
                        +{blog.tags.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Link>
    </article>
  );
};

export default BlogCard; 